import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Search, Pencil, Trash2, X, Star, Crosshair, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { OVERLINE, H1, StatusBadge, btnPrimary, btnSecondary, useAdmin } from "@/components/admin/ui";

const EMPTY = { title: "", species: "", jurisdiction: "", area: "", season: "", start_date: "", end_date: "", method: "", weapon: "", terrain: "", hunt_style: "", duration_days: "", resident_info: "", application_deadline: "", draw_info: "", description: "", tags: "", status: "published", featured: false, published: true };
const toForm = (h) => ({ ...EMPTY, ...h, tags: Array.isArray(h.tags) ? h.tags.join(", ") : (h.tags || "") });
const toPayload = (f) => ({ ...f, duration_days: f.duration_days ? Number(f.duration_days) : null, tags: f.tags.split(",").map((s) => s.trim()).filter(Boolean) });

const Field = ({ label, children }) => (<label className="block"><span className="text-[11px] font-serif uppercase tracking-[0.12em] text-[#5C6B61]">{label}</span>{children}</label>);
const inp = "w-full border border-[#E5E2DC] bg-white px-3 py-2 mt-1 text-sm outline-none focus:border-[#1F2A22]";

function HuntModal({ hunt, onClose, onSaved }) {
  const [f, setF] = useState(hunt ? toForm(hunt) : EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.title.trim()) return toast.error("Title is required");
    setBusy(true);
    try { if (hunt?.id) await api.adminHuntUpdate(hunt.id, toPayload(f)); else await api.adminHuntCreate(toPayload(f)); toast.success("Saved"); onSaved(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50" data-testid="hunt-modal">
      <div className="absolute inset-0 bg-[#1F2A22]/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-lg bg-[#F9F8F6] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E2DC] bg-white sticky top-0">
          <h3 className="font-serif text-xl text-[#1F2A22]">{hunt ? "Edit hunt" : "New hunt"}</h3>
          <button onClick={onClose} aria-label="Close" data-testid="hunt-modal-close"><X size={20} className="text-[#5C6B61]" /></button>
        </div>
        <div className="p-6 space-y-3">
          <Field label="Title"><input value={f.title} onChange={set("title")} className={inp} data-testid="hunt-title" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Species"><input value={f.species} onChange={set("species")} className={inp} /></Field>
            <Field label="State / Jurisdiction"><input value={f.jurisdiction} onChange={set("jurisdiction")} className={inp} /></Field>
            <Field label="Area / Unit"><input value={f.area} onChange={set("area")} className={inp} /></Field>
            <Field label="Season"><input value={f.season} onChange={set("season")} className={inp} /></Field>
            <Field label="Start date"><input type="date" value={f.start_date} onChange={set("start_date")} className={inp} /></Field>
            <Field label="End date"><input type="date" value={f.end_date} onChange={set("end_date")} className={inp} /></Field>
            <Field label="Weapon / Method"><input value={f.weapon || f.method} onChange={set("weapon")} className={inp} /></Field>
            <Field label="Terrain"><input value={f.terrain} onChange={set("terrain")} className={inp} /></Field>
            <Field label="Application deadline"><input value={f.application_deadline} onChange={set("application_deadline")} className={inp} /></Field>
            <Field label="Duration (days)"><input type="number" value={f.duration_days} onChange={set("duration_days")} className={inp} /></Field>
          </div>
          <Field label="Draw information"><textarea rows={2} value={f.draw_info} onChange={set("draw_info")} className={inp} /></Field>
          <Field label="Resident / non-resident info"><textarea rows={2} value={f.resident_info} onChange={set("resident_info")} className={inp} /></Field>
          <Field label="Description"><textarea rows={3} value={f.description} onChange={set("description")} className={inp} /></Field>
          <Field label="Tags (comma separated)"><input value={f.tags} onChange={set("tags")} className={inp} /></Field>
          <div className="flex items-center gap-4 pt-1">
            <Field label="Status"><select value={f.status} onChange={set("status")} className={inp} data-testid="hunt-status"><option value="published">published</option><option value="draft">draft</option><option value="planning">planning</option></select></Field>
            <label className="flex items-center gap-2 text-sm text-[#1F2A22] mt-5"><input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} /> Featured</label>
          </div>
          <button onClick={save} disabled={busy} className={`${btnPrimary} w-full`} data-testid="hunt-save">{busy ? <Loader2 size={15} className="animate-spin" /> : "Save hunt"}</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminHunts() {
  const { admin } = useAdmin();
  const canEdit = admin?.permissions?.includes("hunts");
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(undefined);
  const load = useCallback(() => { setData(null); api.adminHunts({ q: q || undefined }).then(setData).catch(() => setData({ hunts: [], total: 0 })); }, [q]);
  useEffect(() => { load(); }, [load]);
  const del = async (h) => { if (!window.confirm(`Delete "${h.title}"?`)) return; try { await api.adminHuntDelete(h.id); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };

  return (
    <div className="p-6 md:p-8" data-testid="admin-hunts">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div><span className={OVERLINE}>Content</span><h1 className={`${H1} mt-1`}>Hunts</h1></div>
        {canEdit && <button onClick={() => setEditing(null)} className={btnPrimary} data-testid="hunt-new"><Plus size={15} /> New hunt</button>}
      </div>
      <div className="relative mt-6 max-w-xs">
        <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5C6B61]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search hunts…" className="border border-[#E5E2DC] bg-white pl-8 pr-3 py-2 text-sm outline-none focus:border-[#1F2A22] w-full" data-testid="hunt-search" />
      </div>

      {!data ? <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#1F2A22]" /></div>
        : data.hunts.length === 0 ? <div className="mt-8 border border-dashed border-[#E5E2DC] bg-white p-16 text-center text-[#5C6B61]"><Crosshair size={28} className="mx-auto mb-2 text-[#E5E2DC]" />No hunts yet. Create your first.</div>
        : (
        <div className="mt-5 bg-white border border-[#E5E2DC] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F2F0EB] text-[#5C6B61] uppercase tracking-wide text-[11px]"><tr>{["Title", "Species", "State", "Method", "Status", ""].map((h) => <th key={h} className="text-left font-serif p-3">{h}</th>)}</tr></thead>
            <tbody>
              {data.hunts.map((h) => (
                <tr key={h.id} className="border-t border-[#E5E2DC]" data-testid="hunt-row">
                  <td className="p-3 font-medium text-[#1F2A22]">{h.title}{h.featured && <Star size={12} className="inline ml-1.5 text-[#FF6A00] fill-[#FF6A00]" />}</td>
                  <td className="p-3 text-[#5C6B61]">{h.species || "—"}</td>
                  <td className="p-3 text-[#5C6B61]">{h.jurisdiction || "—"}</td>
                  <td className="p-3 text-[#5C6B61]">{h.weapon || h.method || "—"}</td>
                  <td className="p-3"><StatusBadge status={h.status} /></td>
                  <td className="p-3">
                    {canEdit && (
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => setEditing(h)} className="p-1.5 text-[#5C6B61] hover:text-[#1F2A22]" aria-label="Edit" data-testid="hunt-edit"><Pencil size={15} /></button>
                        <button onClick={() => api.adminHuntUpdate(h.id, { featured: !h.featured }).then(load)} className={`p-1.5 ${h.featured ? "text-[#FF6A00]" : "text-[#5C6B61]"} hover:text-[#FF6A00]`} aria-label="Feature" data-testid="hunt-feature"><Star size={15} /></button>
                        <button onClick={() => del(h)} className="p-1.5 text-[#5C6B61] hover:text-[#D9381E]" aria-label="Delete" data-testid="hunt-delete"><Trash2 size={15} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing !== undefined && <HuntModal hunt={editing} onClose={() => setEditing(undefined)} onSaved={load} />}
    </div>
  );
}
